import { useEffect, useRef } from "react";

interface UseInfiniteScrollParams {
  /** Whether more pages remain to load. */
  hasMore: boolean;
  /** A load is already in flight — don't trigger another. */
  isLoading: boolean;
  /** Fired when the sentinel scrolls into view and more can be loaded. */
  onLoadMore: () => void;
  /** Gate observation (e.g. only while a drawer is open). */
  enabled?: boolean;
  /** Prefetch distance before the sentinel is fully visible. */
  rootMargin?: string;
}

/**
 * IntersectionObserver-based infinite scroll.
 *
 * Attach `rootRef` to the scrollable container and `sentinelRef` to an empty
 * element rendered at the end of the list. When the sentinel enters the
 * container's view — and there's more to load and nothing already loading —
 * `onLoadMore` fires.
 *
 * Pass a stable `onLoadMore` (e.g. React Query's `fetchNextPage`, which is
 * referentially stable) so the observer isn't recreated every render.
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  enabled = true,
  rootMargin = "120px",
}: UseInfiniteScrollParams) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled || !hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      // Falls back to the viewport when no scroll container is attached.
      { root: rootRef.current ?? null, rootMargin },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, hasMore, isLoading, onLoadMore, rootMargin]);

  return { rootRef, sentinelRef };
};
